import {useState, useCallback, useMemo} from 'react'
import {useQuery} from 'react-query'
import {initialWPData, WPData} from '../../../@types'
import {DateRangeType, LoadingState, PeriodType} from '../../../../global'
import {usePeriod} from '../usePeriod'
import {Get} from '../../../../setup'

const ENDPOINT = process.env.REACT_APP_WORDPRESS_CLIENT_REPORTS ?? 'wp_client_reports'

interface UpdateSection {
    key: keyof WPData
    length: number
    title: string
}

interface UseWordPressUpdatesReturn {
    data: WPData
    loading: LoadingState
    sortedSections: UpdateSection[]
    handleDateRangeChange: (dateRange: DateRangeType) => void
    period: PeriodType
}

export const useWordPressUpdates = (
    initialDateRange: DateRangeType,
): UseWordPressUpdatesReturn => {
    const [selectedDateRange, setSelectedDateRange] = useState(initialDateRange)
    const [loading, setLoading] = useState<LoadingState>('idle')
    const [data, setData] = useState<WPData>(initialWPData)

    const period = usePeriod(selectedDateRange)

    const handleDateRangeChange = useCallback((dateRange: DateRangeType) => {
        setSelectedDateRange(dateRange)
    }, [])

    useQuery(
        ['DASHBOARD_WP', period],
        () => Get<{data: WPData}>({endpoint: ENDPOINT, period}),
        {
            onSuccess: ({data: wpData}) => {
                setLoading('fulfilled')
                setData(wpData)
            },
            staleTime: 5 * 60 * 1000,
            enabled: Boolean(period),
        },
    )

    const sortedSections = useMemo(() => {
        const sections: UpdateSection[] = [
            {key: 'wp', length: data.wp.length, title: 'Corps WordPress'},
            {key: 'plugin', length: data.plugin.length, title: 'Modules'},
            {key: 'theme', length: data.theme.length, title: 'Thèmes'},
        ]
        return sections.sort((a, b) => b.length - a.length)
    }, [data.wp.length, data.plugin.length, data.theme.length])

    return {
        data,
        loading,
        sortedSections,
        handleDateRangeChange,
        period,
    }
}